// Clip Editor — split from SponsorHQ.jsx · real music tracks · backend persistence · AI overlay headline
import { useState, useRef, useEffect } from "react";
import api from "../../lib/api";
import { Scissors, Type, Volume2, Image as ImageIcon, Download, Sparkles, Wand2, Trash2, Play, Pause, Camera, X } from "lucide-react";
import { toast } from "sonner";

// Royalty-free demo tracks (SoundHelix CDN — reliably hot-linkable)
const TRACKS = [
  { id: "none",    label: "None (use video audio)", url: "" },
  { id: "uplift",  label: "Uplifting · upbeat",     url: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3" },
  { id: "drama",   label: "Cinematic · dramatic",   url: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-2.mp3" },
  { id: "pulse",   label: "Pulse · electronic",     url: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-3.mp3" },
  { id: "chill",   label: "Chill · lofi",           url: "https://www.soundhelix.com/examples/mp3/SoundHelix-Song-4.mp3" },
];

const POSITION_CLASS = {
  top:    "top-3 left-1/2 -translate-x-1/2",
  middle: "top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
  bottom: "bottom-4 left-1/2 -translate-x-1/2",
};

export default function ClipEditorSection() {
  const [clip, setClip] = useState(null);
  const [name, setName] = useState("Untitled reel ad");
  const [overlayText, setOverlayText] = useState("Limited drop — live now");
  const [overlayColor, setOverlayColor] = useState("#FFC107");
  const [textPosition, setTextPosition] = useState("bottom");
  const [duration, setDuration] = useState(15);
  const [soundOn, setSoundOn] = useState(true);
  const [musicTrack, setMusicTrack] = useState("none");
  const [musicPlaying, setMusicPlaying] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiBrand, setAiBrand] = useState("");
  const [aiTone, setAiTone] = useState("punchy");
  const [saving, setSaving] = useState(false);
  const [creatives, setCreatives] = useState([]);
  // Auto-Reel state
  const [arOpen, setArOpen] = useState(false);
  const [arBusy, setArBusy] = useState(false);
  const [arProductImage, setArProductImage] = useState(null);
  const [arProduct, setArProduct] = useState("");
  const [arShots, setArShots] = useState([]);
  const productFileRef = useRef(null);
  const videoRef = useRef(null);
  const fileRef = useRef(null);
  const audioRef = useRef(null);

  const trackUrl = TRACKS.find((t) => t.id === musicTrack)?.url || "";

  const loadCreatives = () =>
    api.get("/sponsor/clip-creative").then(({ data }) => setCreatives(data)).catch(() => setCreatives([]));

  useEffect(() => { loadCreatives(); }, []);

  // Stop music when track changes / preview stops
  useEffect(() => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    setMusicPlaying(false);
  }, [musicTrack]);

  const pickClip = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 20_000_000) { toast.error("Max 20MB"); return; }
    const r = new FileReader();
    r.onload = () => setClip(r.result);
    r.readAsDataURL(f);
  };

  const playPreview = () => {
    if (!videoRef.current) { toast.error("Upload a clip first"); return; }
    videoRef.current.currentTime = 0;
    videoRef.current.muted = !soundOn || musicTrack !== "none";  // mute clip if music is on
    videoRef.current.play();
    if (trackUrl && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().then(() => setMusicPlaying(true)).catch(() => {});
    }
    setTimeout(() => {
      if (videoRef.current) videoRef.current.pause();
      if (audioRef.current) { audioRef.current.pause(); setMusicPlaying(false); }
    }, duration * 1000);
  };

  const toggleMusic = () => {
    if (!audioRef.current || !trackUrl) return;
    if (musicPlaying) { audioRef.current.pause(); setMusicPlaying(false); }
    else { audioRef.current.play().then(() => setMusicPlaying(true)).catch(() => toast.error("Browser blocked autoplay — tap again")); }
  };

  const suggestHeadline = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/sponsor/clip-headline", {
        topic: name || overlayText, brand: aiBrand || undefined, tone: aiTone,
      });
      // Strip markdown code fences if present
      const cleaned = String(data.raw || "").replace(/^```(?:json)?\s*/i, "").replace(/```\s*$/i, "").trim();
      try {
        const j = JSON.parse(cleaned);
        if (j.headline) setOverlayText(j.headline);
        if (j.overlay_color) setOverlayColor(j.overlay_color);
        toast.success("ED3N suggested an overlay" + (data.fallback ? " (offline fallback)" : ""));
      } catch { toast.error("AI returned non-JSON — please tweak the headline manually"); }
    } catch { toast.error("AI assist failed"); }
    setAiBusy(false);
  };

  const saveCreative = async () => {
    if (!clip) { toast.error("Upload a clip first"); return; }
    setSaving(true);
    try {
      await api.post("/sponsor/clip-creative", {
        name, overlay_text: overlayText, overlay_color: overlayColor,
        text_position: textPosition, duration, sound_on: soundOn,
        music_track: musicTrack, video_b64: clip,
      });
      toast.success("Creative saved · use in Banner Editor → 'logo / creative'");
      loadCreatives();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const deleteCreative = async (cid) => {
    try {
      await api.delete(`/sponsor/clip-creative/${cid}`);
      toast.success("Deleted");
      loadCreatives();
    } catch { toast.error("Delete failed"); }
  };

  // === AUTO-REEL: one product photo → 4 AI-generated reel frames ===
  const pickProductPhoto = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 4_000_000) { toast.error("Max 4MB product photo"); return; }
    const r = new FileReader();
    r.onload = () => setArProductImage(r.result);
    r.readAsDataURL(f);
  };

  const runAutoReel = async () => {
    if (!arProductImage) { toast.error("Upload a product photo first"); return; }
    if (!arProduct.trim()) { toast.error("Describe the product (e.g. 'Aurora energy drink')"); return; }
    setArBusy(true); setArShots([]);
    try {
      const { data } = await api.post("/sponsor/auto-reel", {
        product_image_b64: arProductImage,
        product: arProduct,
        brand: aiBrand || undefined,
        tone: aiTone,
      });
      setArShots(data.shots || []);
      if (data.shots?.length) toast.success(`ED3N + Nano Banana generated ${data.shots.length} shots`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Auto-Reel failed");
    }
    setArBusy(false);
  };

  const applyShotAsOverlay = (shot) => {
    setOverlayText(shot.headline);
    toast.success("Headline applied — overlay updated");
  };

  return (
    <section className="space-y-4" data-testid="clip-editor-section">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-display text-sm tracking-[0.3em] uppercase flex items-center gap-2"><Scissors className="w-4 h-4 text-nexus-magenta" /> Clip Editor · Reels Ad Creative</h2>
        <button onClick={saveCreative} disabled={saving} className="btn-nexus text-[10px]" data-testid="clip-export">
          <Download className="w-3 h-3" /> {saving ? "Saving…" : "Save creative"}
        </button>
      </div>
      <p className="text-[11px] text-white/40 leading-relaxed max-w-2xl">
        Build a 15s reel ad: drop a video, add a text headline (or let ED3N AI write one), pick background music. Saved creatives appear below and can be attached to any campaign in the Banner Editor.
      </p>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* LEFT — controls */}
        <div className="lg:col-span-2 space-y-3">
          <div className="bg-nexus-surface p-4 space-y-3 border border-white/10">
            <div>
              <div className="label-eyebrow mb-1">creative name</div>
              <input value={name} onChange={(e) => setName(e.target.value)} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="clip-name" />
            </div>

            <div>
              <div className="label-eyebrow mb-1">video clip (max 20MB)</div>
              <input ref={fileRef} type="file" accept="video/*" className="hidden" onChange={pickClip} />
              <button onClick={() => fileRef.current?.click()} className="btn-ghost text-[10px] w-full justify-center" data-testid="clip-pick">
                <ImageIcon className="w-3 h-3" /> {clip ? "Replace clip" : "Upload clip"}
              </button>
            </div>

            <div>
              <div className="label-eyebrow mb-1 flex items-center gap-2"><Type className="w-3 h-3" /> overlay text</div>
              <input value={overlayText} onChange={(e) => setOverlayText(e.target.value)} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="clip-text" />
            </div>

            {/* AI HEADLINE SUGGESTER */}
            <div className="border border-nexus-violet/40 bg-nexus-violet/5 p-3 space-y-2" data-testid="ai-headline-block">
              <div className="flex items-center gap-2">
                <Wand2 className="w-3.5 h-3.5 text-nexus-violet" />
                <div className="label-eyebrow text-nexus-violet">// ed3n · ai overlay writer</div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input value={aiBrand} onChange={(e) => setAiBrand(e.target.value)} placeholder="Brand (optional)" className="bg-transparent border border-white/10 px-2 py-1.5 text-xs" data-testid="ai-brand" />
                <select value={aiTone} onChange={(e) => setAiTone(e.target.value)} className="bg-nexus-surface border border-white/10 px-2 py-1.5 text-xs" data-testid="ai-tone">
                  <option value="punchy">Punchy</option>
                  <option value="luxury">Luxury</option>
                  <option value="hype">Hype</option>
                  <option value="playful">Playful</option>
                </select>
              </div>
              <button onClick={suggestHeadline} disabled={aiBusy} className="btn-ghost w-full justify-center text-[10px] !border-nexus-violet !text-nexus-violet" data-testid="ai-suggest-headline">
                <Sparkles className="w-3 h-3" /> {aiBusy ? "ED3N thinking…" : "Suggest 15s overlay"}
              </button>
            </div>

            {/* AUTO-REEL — one-click 4-shot reel from a product photo */}
            <button
              onClick={() => setArOpen(true)}
              className="btn-ghost w-full justify-center text-[10px] !border-nexus-amber !text-nexus-amber"
              data-testid="open-auto-reel"
            >
              <Camera className="w-3 h-3" /> ✨ Auto-Reel · 4 shots from 1 photo
            </button>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="label-eyebrow mb-1">text color</div>
                <input type="color" value={overlayColor} onChange={(e) => setOverlayColor(e.target.value)} className="w-full h-10 bg-transparent border border-white/10" />
              </div>
              <div>
                <div className="label-eyebrow mb-1">position</div>
                <select value={textPosition} onChange={(e) => setTextPosition(e.target.value)} className="w-full bg-nexus-surface border border-white/10 px-3 py-2 text-sm">
                  <option value="top">Top</option><option value="middle">Middle</option><option value="bottom">Bottom</option>
                </select>
              </div>
            </div>

            <div>
              <div className="label-eyebrow mb-1">duration · {duration}s</div>
              <input type="range" min={5} max={30} step={1} value={duration} onChange={(e) => setDuration(parseInt(e.target.value, 10))} className="w-full" data-testid="clip-duration" />
            </div>

            <div className="border-t border-white/10 pt-3 space-y-2">
              <div className="label-eyebrow flex items-center gap-2"><Volume2 className="w-3 h-3" /> audio</div>
              <label className="flex items-center gap-2 text-xs text-white/70">
                <input type="checkbox" checked={soundOn} onChange={(e) => setSoundOn(e.target.checked)} className="accent-nexus-cyan" data-testid="clip-sound-toggle" />
                Sound on by default
              </label>
              <div className="flex gap-2">
                <select value={musicTrack} onChange={(e) => setMusicTrack(e.target.value)} className="flex-1 bg-nexus-surface border border-white/10 px-3 py-2 text-xs" data-testid="clip-music">
                  {TRACKS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
                {trackUrl && (
                  <button onClick={toggleMusic} className="btn-ghost text-[10px] px-3" data-testid="clip-music-preview" aria-label="Preview track">
                    {musicPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
                  </button>
                )}
              </div>
              {trackUrl && (
                <audio ref={audioRef} src={trackUrl} preload="metadata" onEnded={() => setMusicPlaying(false)} className="hidden" />
              )}
            </div>

            <button onClick={playPreview} className="btn-ghost text-[10px] w-full justify-center" data-testid="clip-preview-btn">
              <Sparkles className="w-3 h-3" /> Preview {duration}s
            </button>
          </div>

          {/* SAVED CREATIVES */}
          {creatives.length > 0 && (
            <div className="bg-nexus-surface border border-white/10 p-3 space-y-2" data-testid="saved-creatives">
              <div className="label-eyebrow">// your saved creatives</div>
              <div className="space-y-1">
                {creatives.map((c) => (
                  <div key={c.id} className="flex items-center gap-2 text-xs bg-black/30 px-2 py-1.5" data-testid={`creative-${c.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{c.name}</div>
                      <div className="text-[10px] text-white/40">{c.duration}s · {c.text_position} · {c.music_track}</div>
                    </div>
                    <button onClick={() => deleteCreative(c.id)} className="text-nexus-crimson/70 hover:text-nexus-crimson" aria-label="Delete">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT — preview */}
        <div className="lg:col-span-3 space-y-2">
          <div className="label-eyebrow text-nexus-cyan">// live preview · 9:16 reel</div>
          <div className="relative bg-black border border-white/10 mx-auto" style={{ maxWidth: 360, aspectRatio: "9/16" }} data-testid="clip-preview">
            {clip ? (
              <video ref={videoRef} src={clip} className="absolute inset-0 w-full h-full object-cover" muted={!soundOn || musicTrack !== "none"} playsInline loop />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-white/30 text-xs tracking-wider">Upload a clip to preview</div>
            )}
            {overlayText && (
              <div className={`absolute ${POSITION_CLASS[textPosition]} px-3 py-1 text-center max-w-[80%]`} data-testid="clip-overlay">
                <div className="font-display font-black text-xl tracking-tight" style={{ color: overlayColor, textShadow: "0 2px 8px rgba(0,0,0,0.85)" }}>
                  {overlayText}
                </div>
              </div>
            )}
            <div className="absolute top-2 left-2 px-2 py-0.5 text-[8px] tracking-[0.3em] font-display font-black uppercase bg-nexus-amber text-black">★ Sponsored</div>
          </div>
        </div>
      </div>

      {/* AUTO-REEL MODAL */}
      {arOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4" onClick={() => !arBusy && setArOpen(false)}>
          <div className="glass-strong w-full max-w-5xl max-h-[92vh] overflow-y-auto p-6 space-y-5" onClick={(e) => e.stopPropagation()} data-testid="auto-reel-modal">
            <div className="flex items-center justify-between">
              <div>
                <div className="label-eyebrow text-nexus-amber">// auto-reel · ed3n + nano banana</div>
                <h3 className="font-display text-2xl mt-1">One photo → 4-shot reel ad</h3>
                <p className="text-[11px] text-white/50 mt-1">Drop a single product photo. ED3N writes a 4-shot storyboard. Nano Banana repaints your photo for each shot. Apply any headline to your live overlay with one tap.</p>
              </div>
              <button onClick={() => !arBusy && setArOpen(false)} className="text-white/40 hover:text-white" aria-label="close"><X className="w-4 h-4" /></button>
            </div>

            <div className="grid lg:grid-cols-3 gap-5">
              <div className="space-y-3">
                <div>
                  <div className="label-eyebrow mb-1">product photo</div>
                  <input ref={productFileRef} type="file" accept="image/*" className="hidden" onChange={pickProductPhoto} />
                  {arProductImage ? (
                    <div className="relative">
                      <img src={arProductImage} alt="" className="w-full aspect-square object-cover border border-white/10" data-testid="ar-product-preview" />
                      <button onClick={() => productFileRef.current?.click()} className="btn-ghost text-[10px] w-full justify-center mt-2">Replace photo</button>
                    </div>
                  ) : (
                    <button onClick={() => productFileRef.current?.click()} className="btn-ghost text-[10px] w-full justify-center aspect-square" data-testid="ar-pick-photo">
                      <Camera className="w-4 h-4" /> Upload product photo
                    </button>
                  )}
                </div>
                <div>
                  <div className="label-eyebrow mb-1">product description</div>
                  <input value={arProduct} onChange={(e) => setArProduct(e.target.value)} placeholder="e.g. Aurora energy drink can" className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="ar-product-input" />
                </div>
                <div className="text-[10px] text-white/40 leading-relaxed">
                  Uses brand &amp; tone from the AI Overlay Writer above. Generation takes ~20-40s for 4 shots.
                </div>
                <button onClick={runAutoReel} disabled={arBusy || !arProductImage || !arProduct.trim()} className="btn-nexus w-full justify-center" data-testid="ar-generate">
                  <Sparkles className="w-3.5 h-3.5" /> {arBusy ? "Generating 4 shots…" : "Generate reel"}
                </button>
              </div>

              <div className="lg:col-span-2 space-y-3">
                <div className="label-eyebrow text-nexus-cyan">// generated shots (tap to apply headline)</div>
                {arShots.length === 0 && !arBusy && (
                  <div className="border border-dashed border-white/10 p-10 text-center text-white/30 text-sm">Your 4 generated shots will appear here.</div>
                )}
                {arBusy && (
                  <div className="border border-nexus-amber/40 p-10 text-center text-nexus-amber text-sm animate-pulse">ED3N scripting · Nano Banana rendering… hang tight.</div>
                )}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="ar-shots-grid">
                  {arShots.map((shot, i) => (
                    <button
                      key={i}
                      onClick={() => applyShotAsOverlay(shot)}
                      className="relative aspect-[9/16] bg-black border border-white/10 overflow-hidden group hover:border-nexus-amber transition-colors"
                      data-testid={`ar-shot-${i}`}
                    >
                      <img src={shot.image_b64} alt="" className="absolute inset-0 w-full h-full object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                      <div className="absolute bottom-2 left-2 right-2 text-center">
                        <div className="font-display font-black text-sm tracking-tight" style={{ color: "#FFC107", textShadow: "0 2px 6px rgba(0,0,0,0.85)" }}>
                          {shot.headline}
                        </div>
                      </div>
                      <div className="absolute top-2 left-2 px-1.5 py-0.5 text-[8px] tracking-[0.2em] font-display bg-nexus-amber text-black">SHOT {i + 1}</div>
                      <div className="absolute inset-0 flex items-end justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="bg-nexus-amber text-black px-2 py-1 text-[9px] tracking-[0.2em] uppercase font-display mb-8">Apply headline →</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
